import React from 'react';
import CurrencyInput from '../../../molecules/CurrencyInput';
import { FaTimes } from 'react-icons/fa';

export interface ContractGuaranteeData {
    contract_value: number;
    anticipated_value: number;
    file: File | null;
    existing_file: string;
}

interface ContractGuaranteeManagerProps {
    value: ContractGuaranteeData;
    onChange: (data: ContractGuaranteeData) => void;
    onFileDownload?: (fileName: string) => void;
    disabled?: boolean;
    errorFields?: Array<{ fieldName: string; message: string }>;
}

const getError = (errorFields: ContractGuaranteeManagerProps['errorFields'], field: string) =>
    errorFields?.find(e => e.fieldName === field)?.message || '';

const ContractGuaranteeManager: React.FC<ContractGuaranteeManagerProps> = ({
    value,
    onChange,
    onFileDownload,
    disabled,
    errorFields
}) => {
    const handleFileChange = (file: File | null) => {
        onChange({ ...value, file, existing_file: file ? '' : value.existing_file });
    };

    const handleRemoveExistingFile = () => {
        onChange({ ...value, existing_file: '', file: null });
    };

    return (
        <div className="mt-6">
            <h2 className="text-xl font-semibold mb-4 dark:text-white">Contrato Garantia</h2>

            <div className="grid grid-cols-12 gap-4 mb-6">
                <div className="col-span-3">
                    <CurrencyInput
                        id="contract_guarantee_value"
                        label="Valor do Contrato"
                        prefix="R$"
                        fractionDigits={2}
                        value={value.contract_value}
                        onChange={(v) => onChange({ ...value, contract_value: v })}
                        disabled={disabled}
                        errorMessage={getError(errorFields, 'contract_guarantee_value')}
                    />
                </div>
                <div className="col-span-3">
                    <CurrencyInput
                        id="contract_guarantee_anticipated_value"
                        label="Valor Antecipado"
                        prefix="R$"
                        fractionDigits={2}
                        value={value.anticipated_value}
                        onChange={(v) => onChange({ ...value, anticipated_value: v })}
                        disabled={disabled}
                        errorMessage={getError(errorFields, 'contract_guarantee_anticipated_value')}
                    />
                </div>
            </div>

            <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Contrato (PDF)
                </label>
                <div
                    className="flex items-center justify-center w-full"
                    onDrop={(e) => {
                        e.preventDefault();
                        const file = e.dataTransfer.files?.[0];
                        if (file && file.type === 'application/pdf') handleFileChange(file);
                    }}
                    onDragOver={(e) => { e.preventDefault(); }}
                >
                    <label
                        htmlFor="contract-guarantee-upload"
                        className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    >
                        <div className="flex flex-col items-center justify-center pt-4 pb-4">
                            <svg className="w-7 h-7 mb-2 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 20 16">
                                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2" />
                            </svg>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                <span className="font-semibold">Upload do Contrato Garantia (PDF)</span>
                            </p>
                        </div>
                        <input
                            id="contract-guarantee-upload"
                            type="file"
                            className="hidden"
                            accept=".pdf"
                            disabled={disabled}
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleFileChange(file);
                            }}
                        />
                    </label>
                </div>

                {value.existing_file && !value.file && (
                    <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-800 border border-blue-200 dark:border-blue-600 rounded flex justify-between items-center">
                        <p className="text-sm text-blue-700 dark:text-blue-100">
                            <strong>Arquivo atual:</strong>{' '}
                            <button
                                onClick={() => onFileDownload?.(value.existing_file)}
                                className="underline hover:text-blue-900 dark:hover:text-blue-300 cursor-pointer bg-transparent border-0 p-0"
                            >
                                {value.existing_file}
                            </button>
                        </p>
                        {!disabled && (
                            <button
                                onClick={handleRemoveExistingFile}
                                className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
                            >
                                <FaTimes className="inline mr-1" /> Remover
                            </button>
                        )}
                    </div>
                )}

                {value.file && (
                    <div className="mt-2 p-2 bg-green-50 dark:bg-green-800 border border-green-200 dark:border-green-600 rounded flex justify-between items-center">
                        <p className="text-sm text-green-700 dark:text-green-100">
                            <strong>Novo arquivo:</strong> {value.file.name}
                        </p>
                        {!disabled && (
                            <button
                                onClick={() => handleFileChange(null)}
                                className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
                            >
                                <FaTimes className="inline mr-1" /> Remover
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContractGuaranteeManager;
